import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  MdDashboard,
  MdFolder,
  MdPeople,
  MdAccessTime,
  MdBarChart,
  MdSettings,
  MdLogout,
  MdPerson,
  IoMdWallet,
  MdBusiness,
  MdShowChart,
  MdReceiptLong,
  MdQrCodeScanner,
  MdManageSearch,
  MdLocalOffer,
  MdModeEditOutline,
} from "react-icons/md";
import { TbPageBreak } from "react-icons/tb";
import { updateLogoApi, getProfileApi } from "../api/profileApi";
import { toast } from "react-toastify";
import "../styles/Sidebar.css";

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { sidebarCollapsed } = useSelector((state) => state.ui);
  const { user } = useSelector((state) => state.auth);
  const [uploading, setUploading] = useState(false);
  const [profileData, setProfileData] = useState(null);

  const logo =
    profileData?.logo ||
    user?.almataPartner?.logo ||
    user?.logo ||
    "https://washi.s3.amazonaws.com/RiskReport360.png";
  const companyName = "Almata Partner";
  const almataId = user?.almataPartner?.id || user?.id || user?._id;

  // Fetch profile once on mount
  useEffect(() => {
    const fetchProfile = async () => {
      if (almataId && !profileData) {
        try {
          const response = await getProfileApi(almataId);
          if (response.status === 200) {
            setProfileData(response.response);
          }
        } catch (error) {
          console.error("Failed to fetch profile:", error);
        }
      }
    };
    fetchProfile();
  }, [almataId, profileData]);

  // Listen for logo updates from Profile component
  useEffect(() => {
    const handleLogoUpdate = async () => {
      if (almataId) {
        try {
          const response = await getProfileApi(almataId);
          if (response.status === 200) {
            setProfileData(response.response);
          }
        } catch (error) {
          console.error("Failed to refresh profile:", error);
        }
      }
    };

    window.addEventListener("logoUpdated", handleLogoUpdate);
    return () => window.removeEventListener("logoUpdated", handleLogoUpdate);
  }, [almataId]);

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("logo", file);
    formData.append("almataId", almataId);

    try {
      const response = await updateLogoApi(formData);
      if (response.status === 200) {
        toast.success("Logo updated successfully!");
        // Fetch updated profile data
        const profileResponse = await getProfileApi(almataId);
        if (profileResponse.status === 200) {
          setProfileData(profileResponse.response);
        }
        // Notify Profile component about logo update
        window.dispatchEvent(
          new CustomEvent("logoUpdated", { detail: { logo: response.logo } })
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update logo");
    } finally {
      setUploading(false);
    }
  };

  const menuItems = [
    { path: "/", icon: MdDashboard, label: "Dashboard" },
    { path: "/oem-partners", icon: MdBusiness, label: "OEM Partners" },
    { path: "/revenue", icon: MdShowChart, label: "Revenue" },
    { path: "/transactions", icon: MdReceiptLong, label: "Transaction" },
    { path: "/OEMScan", icon: MdQrCodeScanner, label: "OEM Scan" },
    { path: "/scan", icon: MdManageSearch, label: "My Scan" },
    { path: "/all-coupons", icon: MdLocalOffer, label: "Coupons" },
    { path: "/oem-myscan-list", icon: TbPageBreak, label: "OEM Scan Logs" },
    
  ];

  return (
    <div className={`sidebar ${sidebarCollapsed ? "collapsed" : ""}`}>
      <div className="sidebar-header">
        <div className="logo" style={{ position: "relative" }}>
          <img
            src={logo}
            alt={`${companyName} Logo`}
            className="sidebar-logo"
            onClick={() => navigate("/")}
          />
          <input
            type="file"
            accept="image/*"
            onChange={handleLogoUpload}
            style={{ display: "none" }}
            id="sidebar-logo-upload"
            disabled={uploading}
          />
          <label
            htmlFor="sidebar-logo-upload"
            className="logo-edit-btn"
            title={uploading ? "Uploading..." : "Change Logo"}
            style={{
              position: "absolute",
              top: "0px",
              right: "0px",
              background: "white",
              color: "black",
              borderRadius: "50%",
              width: "24px",
              height: "24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: uploading ? "not-allowed" : "pointer",
              fontSize: "12px",
              border: "2px solid white",
              opacity: "0",
              transition: "opacity 0.2s ease",
              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            }}
          >
            {uploading ? "..." : <MdModeEditOutline />}
          </label>
        </div>
      </div>

      <nav className="sidebar-nav">
        {menuItems.map((item) => {
          const IconComponent = item.icon;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-item ${
                location.pathname === item.path ? "active" : ""
              }`}
              data-tooltip={item.label}
              title={sidebarCollapsed ? item.label : ""}
              onMouseEnter={(e) => {
                if (sidebarCollapsed) {
                  const rect = e.currentTarget.getBoundingClientRect();
                  document.documentElement.style.setProperty(
                    "--tooltip-top",
                    `${rect.top + rect.height / 2}px`
                  );
                }
              }}
            >
              <span className="nav-icon">
                <IconComponent />
              </span>
              <span className="nav-label">{item.label}</span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
};

export default Sidebar;
